/**
 * User roles matching the backend Prisma schema
 */
export enum Role {
  SystemAdmin = "SystemAdmin",
  AccountAdmin = "AccountAdmin",
  User = "User",
  LocationUser = "LocationUser",
  TechnicalUser = "TechnicalUser",
  TechnicalManager = "TechnicalManager",
  ApiUser = "ApiUser",
}

/**
 * Helper function to check if a user is a system admin
 */
export function isSystemAdmin(role?: string | null): boolean {
  return role === Role.SystemAdmin;
}

/**
 * Helper function to check if a user is an account admin
 */
export function isAccountAdmin(role?: string | null): boolean {
  return role === Role.AccountAdmin;
}

/**
 * Helper function to check if a user is a standard user
 */
export function isUser(role?: string | null): boolean {
  return role === Role.User;
}

/**
 * Helper function to check if a user is a service technician (ticket-focused, read-only on related entities)
 */
export function isTechnicalUser(role?: string | null): boolean {
  return role === Role.TechnicalUser;
}

/**
 * Helper function to check if a user is a technical manager (account-admin-like with users/settings read-only)
 */
export function isTechnicalManager(role?: string | null): boolean {
  return role === Role.TechnicalManager;
}

/**
 * Helper function to check if a user is a Location User (assigned to exactly one location, Magic URL only).
 */
export function isLocationUser(role?: string | null): boolean {
  return role === Role.LocationUser;
}

/**
 * Helper function to check if a user is an API User (external integration, authenticates via API keys).
 */
export function isApiUser(role?: string | null): boolean {
  return role === Role.ApiUser;
}

/**
 * Helper function to check if a user is a technical role (TechnicalUser or TechnicalManager).
 * These roles only see the technical dashboard and tickets.
 */
export function isTechnicalRole(role?: string | null): boolean {
  return role === Role.TechnicalUser || role === Role.TechnicalManager;
}

/**
 * Helper function to check if a user is a tenant user (AccountAdmin, User, LocationUser, TechnicalUser, or TechnicalManager)
 */
export function isTenantUser(role?: string | null): boolean {
  return (
    role === Role.AccountAdmin ||
    role === Role.User ||
    role === Role.LocationUser ||
    role === Role.TechnicalUser ||
    role === Role.TechnicalManager
  );
}
